import { useMemo, useCallback, useEffect } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'

import { useAuthStore } from '../../../stores/authStore.js'
import { useSelectedMarkets } from '../../../hooks/useSelectedMarkets.js'
import { canManageOrders } from '../../../lib/config/roles.js'
import { isDrayNowHost } from '../../../lib/config/multitenancy.js'
import {
  DEFAULT_PER_PAGE,
  LIST_TYPES,
  STORAGE_KEYS,
  getDefaultSortState,
} from '../utils/marketplaceConstants.js'
import {
  serializeFiltersToParams,
  deserializeFiltersFromParams,
} from '../components/MarketplaceFilters/filterTransformers.js'

/**
 * useMarketplaceParams
 *
 * Hook for parsing and managing marketplace URL parameters.
 * Provides a centralized way to read/write URL state for:
 * - markets (comma-separated list or 'all')
 * - category (filter category: open, pending, etc.)
 * - page (pagination)
 * - search query
 * - sort (field:direction)
 * - listType (orders or trips)
 */
export const useMarketplaceParams = () => {
  const params = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const { user } = useAuthStore()

  // Get user role for role-based list type logic
  const userRole = user?.role || 'guest'

  // The overview default sort differs per theme (getDefaultSort). Build-time
  // constant, so memoize once like the other marketplace hooks do.
  const isDrayNow = useMemo(() => isDrayNowHost(), [])

  const { selectedMarkets: markets, marketsCsv } = useSelectedMarkets()

  const category = useMemo(() => {
    return params.filter || params.category || 'open'
  }, [params.filter, params.category])

  const page = useMemo(() => {
    const pageParam = params.page || searchParams.get('page')
    return parseInt(pageParam || '1', 10)
  }, [params.page, searchParams])

  const search = useMemo(() => {
    return searchParams.get('query') || searchParams.get('search') || ''
  }, [searchParams])

  const listType = useMemo(() => {
    // ALLOWLIST — only admin/imc get the orders list. Every other role
    // (carrier, driver, guest, unknown) is forced to trips. This is the legacy
    // rule from src/app/models/Marketplace.js:
    //   ['imc', 'admin'].includes(rol) ? 'orders' : 'trips'
    // Do not turn this back into a `role === 'carrier'` denylist: listType is
    // the gate that keeps non-order roles away from the create-order
    // affordance, and the denylist form let drivers through (see roles.js).
    if (!canManageOrders(userRole)) {
      return LIST_TYPES.TRIPS
    }

    // Admins/IMCs can use URL param to switch between views, default to orders
    const type = searchParams.get('type') || LIST_TYPES.ORDERS
    return type === LIST_TYPES.TRIPS ? LIST_TYPES.TRIPS : LIST_TYPES.ORDERS
  }, [searchParams, userRole])

  const perPage = useMemo(() => {
    const perPageParam = searchParams.get('perPage')
    return perPageParam ? parseInt(perPageParam, 10) : DEFAULT_PER_PAGE
  }, [searchParams])

  // Parse sort state from URL — single-key {field, direction} that drives the
  // column sort indicator. Default is category + list-type + theme aware
  // (legacy Marketplace.defaultSort): closed/canceled default DESC, overview
  // defaults to its first key (alertSeverityOrder on DrayNow, categoryOrder on
  // ModalView — see getDefaultSort), everything else ASC.
  const sortState = useMemo(() => {
    const defaultSort = getDefaultSortState(category, listType, isDrayNow)

    const sortParam = searchParams.get('sort') || ''
    if (!sortParam) {
      return defaultSort
    }
    const [field, direction] = sortParam.split(':')
    return {
      field: field || defaultSort.field,
      direction: (direction || defaultSort.direction).toLowerCase(),
    }
  }, [searchParams, listType, category, isDrayNow])

  const hasExplicitSort = !!searchParams.get('sort')

  // Parse admin filters from URL on every render
  const adminFilters = useMemo(() => {
    return deserializeFiltersFromParams(searchParams)
  }, [searchParams])

  const adminFiltersCount = adminFilters.length

  // Navigation helpers
  const buildMarketplaceUrl = useCallback(
    (overrides = {}) => {
      const newMarkets =
        overrides.markets !== undefined
          ? overrides.markets.length > 0
            ? overrides.markets.join(',')
            : 'all'
          : marketsCsv
      const newCategory = overrides.category ?? category
      const newPage = overrides.page ?? 1
      const newSearch = overrides.search ?? search
      const newType = overrides.listType ?? listType
      // Legacy parity: the Marketplace model reset sort to the new category's
      // defaultSort on every filter change (reaction on `filter`). So changing
      // category drops any active sort and lets the new category's default apply
      // — otherwise the previous category's default leaks in as an explicit
      // param and overrides it (e.g. open's ASC default would override closed's
      // DESC default). Page/search/market changes within a category preserve an
      // explicit user sort.
      const categoryChanged =
        overrides.category !== undefined && overrides.category !== category
      const newSort = categoryChanged ? null : (overrides.sort ?? sortState)

      let url = `/marketplace/${newMarkets}/${newCategory}`
      if (newPage > 1) {
        url += `/${newPage}`
      }

      const queryParams = new URLSearchParams()
      if (newSearch) {
        queryParams.set('query', newSearch)
      }
      if (newType === LIST_TYPES.TRIPS) {
        queryParams.set('type', LIST_TYPES.TRIPS)
      }
      // Omit the sort param only when the full sort state (field + direction)
      // matches the new category's default — keeps URLs clean on first load
      // while still allowing the default field's direction to be toggled (e.g.
      // appointment oldest → newest). Comparing field alone would swallow a
      // direction flip on the default field. A null newSort (category change)
      // never emits a param, so the new category falls back to its default.
      const defaultSort = getDefaultSortState(newCategory, newType, isDrayNow)
      const isDefaultSort =
        !!newSort &&
        newSort.field === defaultSort.field &&
        newSort.direction === defaultSort.direction
      if (newSort && newSort.field && !isDefaultSort) {
        queryParams.set('sort', `${newSort.field}:${newSort.direction}`)
      }

      if (overrides.filterParams !== undefined) {
        Object.entries(overrides.filterParams).forEach(([k, v]) =>
          queryParams.set(k, v),
        )
      } else {
        for (const [key, val] of searchParams.entries()) {
          if (key.startsWith('f_')) {
            queryParams.set(key, val)
          }
        }
      }

      const queryString = queryParams.toString()
      if (queryString) {
        url += `?${queryString}`
      }

      return url
    },
    [
      marketsCsv,
      category,
      search,
      listType,
      sortState,
      searchParams,
      isDrayNow,
    ],
  )

  // Update category (filter) and reset page
  const setCategory = useCallback(
    (newCategory) => {
      const url = buildMarketplaceUrl({ category: newCategory, page: 1 })
      navigate(url)
    },
    [buildMarketplaceUrl, navigate],
  )

  // Update page
  const setPage = useCallback(
    (newPage) => {
      const url = buildMarketplaceUrl({ page: newPage })
      navigate(url)
    },
    [buildMarketplaceUrl, navigate],
  )

  // Update search and reset page
  const setSearch = useCallback(
    (newSearch) => {
      const url = buildMarketplaceUrl({ search: newSearch, page: 1 })
      navigate(url)
    },
    [buildMarketplaceUrl, navigate],
  )

  // Update sort and reset page
  const setSort = useCallback(
    (field, direction) => {
      let nextField = field
      let nextDirection = direction
      // Issue #23: GenericTable's tri-state toggle clears the sort (field=null)
      // on the 3rd click, and here "clear" reverts to the category default. When
      // the active column IS the default sort field at its default direction
      // (closed/canceled default to appointment DESC), clearing reverts to the
      // identical state — a no-op that freezes the toggle. Flip to asc instead so
      // the default column keeps toggling asc <-> desc.
      if (!field) {
        const defaultSort = getDefaultSortState(category, listType, isDrayNow)
        if (
          sortState.field === defaultSort.field &&
          sortState.direction === defaultSort.direction
        ) {
          nextField = sortState.field
          nextDirection = 'asc'
        }
      }
      const sort = nextField
        ? { field: nextField, direction: nextDirection || 'asc' }
        : { field: '', direction: '' }
      const url = buildMarketplaceUrl({ sort, page: 1 })
      navigate(url)
    },
    [buildMarketplaceUrl, navigate, category, listType, sortState, isDrayNow],
  )

  // Update markets and reset page
  const setMarkets = useCallback(
    (newMarkets) => {
      const url = buildMarketplaceUrl({ markets: newMarkets, page: 1 })
      navigate(url)
    },
    [buildMarketplaceUrl, navigate],
  )

  // Update list type (orders/trips) and reset page
  const setListType = useCallback(
    (newListType) => {
      const url = buildMarketplaceUrl({ listType: newListType, page: 1 })
      navigate(url)
    },
    [buildMarketplaceUrl, navigate],
  )

  // Clear search
  const clearSearch = useCallback(() => {
    setSearch('')
  }, [setSearch])

  // Set filters — replaces all filters in URL + localStorage, resets page.
  // Optional `category` override navigates to a different category (used by the
  // pending-driver filter, which only applies in the `tendered` category).
  const setFilters = useCallback(
    (appliedFilters, { category: categoryOverride } = {}) => {
      try {
        localStorage.setItem(
          STORAGE_KEYS.ADMIN_FILTERS,
          JSON.stringify(appliedFilters),
        )
      } catch {
        /* ignore */
      }

      const filterParams = serializeFiltersToParams(appliedFilters)
      const overrides = { filterParams, page: 1 }
      if (categoryOverride) overrides.category = categoryOverride
      const url = buildMarketplaceUrl(overrides)
      navigate(url)
    },
    [buildMarketplaceUrl, navigate],
  )

  // Clear all filters and reset page
  const clearFilters = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.ADMIN_FILTERS, JSON.stringify([]))
    } catch {
      /* ignore */
    }

    const url = buildMarketplaceUrl({ filterParams: {}, page: 1 })
    navigate(url)
  }, [buildMarketplaceUrl, navigate])

  // localStorage fallback on init — if URL has no f_* params but localStorage
  // has filters, apply them to the URL (one-time redirect)
  useEffect(() => {
    const hasUrlFilters = [...searchParams.keys()].some((k) =>
      k.startsWith('f_'),
    )
    if (hasUrlFilters) return // URL is source of truth

    try {
      const stored = localStorage.getItem(STORAGE_KEYS.ADMIN_FILTERS)
      const parsed = stored ? JSON.parse(stored) : []
      if (Array.isArray(parsed) && parsed.length > 0) {
        // Apply localStorage filters to URL
        const filterParams = serializeFiltersToParams(parsed)
        if (Object.keys(filterParams).length > 0) {
          setSearchParams(
            (prev) => {
              const newParams = new URLSearchParams(prev)
              Object.entries(filterParams).forEach(([k, v]) =>
                newParams.set(k, v),
              )
              return newParams
            },
            { replace: true },
          ) // replace to avoid back-button loop
        }
      }
    } catch {
      /* ignore */
    }
  }, [])

  return {
    // Current state (read-only)
    markets,
    marketsCsv,
    category,
    page,
    perPage,
    search,
    listType,
    sortState,
    hasExplicitSort,

    // Admin filters (from URL)
    adminFilters,
    adminFiltersCount,

    // Actions
    setFilters,
    clearFilters,
    setCategory,
    setPage,
    setSearch,
    setSort,
    setMarkets,
    setListType,
    clearSearch,

    // URL building
    buildMarketplaceUrl,
  }
}
